/**
 * FoundationBridgeOpsPanel — Settings → Integrations top-level
 * operator surface for the live Foundation bridge.
 *
 * Three stacked sections:
 *   1. **Bridge health chip** — polls /bridge-health every 30 s,
 *      shows green/red + the bridge's own version/odbc_ok flag, with
 *      a "Test now" button that re-probes synchronously.
 *   2. **Approval matrix** — per-doc-type toggles
 *      (work_order / purchase_order / ar_invoice / change_order).
 *      Saves directly to the backend via PUT.
 *   3. **Dispatch + Sync controls** — two action buttons:
 *      • "Dispatch approved batch" → POSTs /bridge-dispatch and
 *        shows a toast with the sent/failed counts.
 *      • "Sync from Foundation" → POSTs /foundation/sync and refreshes
 *        the sync-status table below.
 *   4. **Sync status table** — per-table last_sync_at, live_count,
 *      and last status. Refreshes after a sync trigger.
 *
 * RBAC: rendered behind the same admin gate as the Approval Queue
 * panel — admins only.
 */
import { useEffect, useState, useCallback } from "react";
import {
  Activity, RefreshCw, Send, ServerCrash, CheckCircle2,
  AlertTriangle, ChevronDown, ChevronRight, Database, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { PanelHeader, CollapsibleSection } from "@/components/settings/SettingsUI";
import FoundationJobSpPanel from "./FoundationJobSpPanel";
import {
  getFoundationBridgeHealth,
  getFoundationBridgeTelemetry,
  getFoundationSchemaCheck,
  dispatchFoundationBridge,
  getFoundationSyncStatus,
  syncFoundationTables,
  getFoundationApprovalSettings,
  updateFoundationApprovalSettings,
  getFoundationProjectTypeMap,
  updateFoundationProjectTypeMap,
  getFoundationWritePermissionCheck,
  backfillWonOrphans,
} from "@/lib/api";

const POLL_HEALTH_MS = 30_000;

const DOC_TYPE_LABELS = {
  work_order:     "Work Orders",
  purchase_order: "Purchase Orders",
  ar_invoice:     "AR Invoices",
  change_order:   "Change Orders",
  jobs_sp:        "Jobs (SP) — sp_insert_update_jobs",
};

function HealthChip({ data }) {
  if (!data) {
    return (
      <span className="text-xs font-semibold bg-slate-100 text-slate-500 px-2 py-1 rounded-full"
            data-testid="bridge-health-chip-loading">
        Checking…
      </span>
    );
  }
  if (!data.configured) {
    return (
      <span className="text-xs font-semibold bg-slate-100 text-slate-500 px-2 py-1 rounded-full inline-flex items-center gap-1"
            data-testid="bridge-health-chip-not-configured">
        <ServerCrash className="w-3 h-3" />
        Not configured
      </span>
    );
  }
  if (data.ok) {
    return (
      <span className="text-xs font-semibold bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full inline-flex items-center gap-1"
            data-testid="bridge-health-chip-ok">
        <CheckCircle2 className="w-3 h-3" />
        Healthy{data.body?.version ? ` · v${data.body.version}` : ""}
      </span>
    );
  }
  return (
    <span className="text-xs font-semibold bg-rose-100 text-rose-700 px-2 py-1 rounded-full inline-flex items-center gap-1 max-w-[420px]"
          data-testid="bridge-health-chip-down" title={data.hint || ""}>
      <AlertTriangle className="w-3 h-3 shrink-0" />
      <span className="truncate">Down · {data.summary || data.status_text || "no response"}</span>
      {(data.detail || data.error) && (
        <details className="inline">
          <summary className="cursor-pointer underline font-normal" data-testid="bridge-health-details-toggle">details</summary>
          <pre className="absolute z-20 mt-1 max-w-[520px] whitespace-pre-wrap text-[10px] font-normal bg-white text-slate-700 border border-slate-200 rounded p-2 shadow" data-testid="bridge-health-details">
            {data.hint ? `${data.hint}\n\n` : ""}{typeof (data.detail || data.error) === "string" ? (data.detail || data.error) : JSON.stringify(data.detail || data.error, null, 2)}
          </pre>
        </details>
      )}
    </span>
  );
}

function fmtAgo(iso) {
  if (!iso) return "never";
  try {
    const dt = new Date(iso);
    const m = (Date.now() - dt.getTime()) / 60000;
    if (m < 1) return "just now";
    if (m < 60) return `${m.toFixed(0)} min ago`;
    if (m < 1440) return `${(m / 60).toFixed(1)} h ago`;
    return `${(m / 1440).toFixed(1)} d ago`;
  } catch { return iso; }
}

// iter214 — human lag: 42s / 3.5m / 2.1h
function fmtLag(seconds) {
  if (seconds === null || seconds === undefined) return "—";
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`;
  return `${(seconds / 3600).toFixed(1)}h`;
}

export default function FoundationBridgeOpsPanel({ isAdmin }) {
  const [health, setHealth] = useState(null);
  const [telemetry, setTelemetry] = useState(null);
  // iter218 — on-demand live schema check
  const [schemaCheck, setSchemaCheck] = useState(null);
  const [schemaChecking, setSchemaChecking] = useState(false);
  const [writePerms, setWritePerms] = useState(null);
  const [writePermChecking, setWritePermChecking] = useState(false);
  const [approvalSettings, setApprovalSettings] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  // iter197 — overall PK integrity roll-up ("ok" / "warning" /
  // "critical" / "unknown"). Rendered as a pill next to the
  // "Foundation cache" section heading so collisions surface here
  // instead of during a quarter-end report.
  const [pkIntegrityOverall, setPkIntegrityOverall] = useState("unknown");
  const [busy, setBusy] = useState(false);

  const loadHealth = useCallback(async () => {
    try { const { data } = await getFoundationBridgeHealth(); setHealth(data); }
    catch { setHealth({ ok: false, configured: false, error: "fetch failed" }); }
  }, []);

  // iter214 — Bridge Health widget data (heartbeat + per-doc-type lag)
  const loadTelemetry = useCallback(async () => {
    try { const { data } = await getFoundationBridgeTelemetry(); setTelemetry(data); }
    catch { setTelemetry(null); }
  }, []);

  // iter218 — probe the live Foundation SQL for table/column drift.
  const runSchemaCheck = useCallback(async () => {
    setSchemaChecking(true);
    try {
      const { data } = await getFoundationSchemaCheck();
      setSchemaCheck(data);
      if (data.overall === "ok") toast.success("Foundation schema OK — all tables & columns present");
      else if (data.overall === "drift") toast.error("Schema drift detected — see details below");
      else if (data.overall === "unconfigured") toast.error("Foundation bridge not configured");
      else toast.warning("Schema check could not complete — bridge unreachable?");
    } catch (e) {
      toast.error("Schema check failed");
      setSchemaCheck({ ok: false, overall: "error", tables: [], error: "request failed" });
    } finally {
      setSchemaChecking(false);
    }
  }, []);

  // iter231 — write-permission audit via HAS_PERMS_BY_NAME.
  const runWritePermCheck = useCallback(async () => {
    setWritePermChecking(true);
    try {
      const { data } = await getFoundationWritePermissionCheck();
      setWritePerms(data);
      if (data.ok) toast.success("Write permissions OK — INSERT granted on all target tables");
      else if (data.error) toast.error("Permission check failed — bridge unreachable?");
      else toast.warning("Missing INSERT grants — see the matrix below");
    } catch (e) {
      toast.error("Permission check failed");
      setWritePerms({ ok: false, error: "request failed", tables: [] });
    } finally {
      setWritePermChecking(false);
    }
  }, []);

  const loadApproval = useCallback(async () => {
    try { const { data } = await getFoundationApprovalSettings(); setApprovalSettings(data.settings); }
    catch { /* leave as null; UI shows skeleton */ }
  }, []);

  const [projectTypeMap, setProjectTypeMap] = useState(null);
  const [savingPtMap, setSavingPtMap] = useState(false);
  const loadProjectTypeMap = useCallback(async () => {
    try { const { data } = await getFoundationProjectTypeMap(); setProjectTypeMap(data.map); }
    catch { /* leave as null; UI shows skeleton */ }
  }, []);

  const handleSaveProjectTypeMap = async () => {
    setSavingPtMap(true);
    try {
      const { data } = await updateFoundationProjectTypeMap(projectTypeMap);
      setProjectTypeMap(data.map);
      toast.success("Project type mapping saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save mapping");
    } finally { setSavingPtMap(false); }
  };

  const loadSyncStatus = useCallback(async () => {
    try {
      const { data } = await getFoundationSyncStatus();
      setSyncStatus(data.tables || []);
      // iter197 — capture the overall PK integrity roll-up so the
      // section header can render a single 🚩 pill.
      setPkIntegrityOverall(data.pk_integrity_overall || "unknown");
    }
    catch { setSyncStatus([]); setPkIntegrityOverall("unknown"); }
  }, []);

  useEffect(() => {
    if (!isAdmin) return undefined;
    loadHealth(); loadTelemetry(); loadApproval(); loadSyncStatus(); loadProjectTypeMap();
    const id = setInterval(loadHealth, POLL_HEALTH_MS);
    return () => clearInterval(id);
  }, [isAdmin, loadHealth, loadTelemetry, loadApproval, loadSyncStatus, loadProjectTypeMap]);

  const handleToggleApproval = async (doc_type, value) => {
    const next = { ...approvalSettings, [doc_type]: value };
    setApprovalSettings(next);  // optimistic
    try {
      const { data } = await updateFoundationApprovalSettings(next);
      setApprovalSettings(data.settings);
      toast.success(`${DOC_TYPE_LABELS[doc_type]} approval ${value ? "required" : "skipped"}`);
    } catch (e) {
      // Roll back on failure.
      setApprovalSettings(approvalSettings);
      toast.error(e?.response?.data?.detail || "Could not update setting");
    }
  };

  const handleDispatch = async () => {
    setBusy(true);
    try {
      const { data } = await dispatchFoundationBridge(25);
      if (data.dispatched === 0) toast.info("No approved rows queued for the bridge.");
      else if (data.failed > 0) toast.warning(`${data.sent} sent · ${data.failed} failed`);
      else toast.success(`${data.sent} pushed to Foundation`);
      loadTelemetry();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dispatch failed");
    } finally {
      setBusy(false);
    }
  };

  const handleSync = async () => {
    setBusy(true);
    try {
      const { data } = await syncFoundationTables();
      const ok = (data.results || []).filter(r => r.status === "ok").length;
      const err = (data.results || []).filter(r => r.status === "error").length;
      toast.success(`Sync: ${ok} OK · ${err} errors`);
      loadSyncStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="foundation-bridge-ops-section">
      <PanelHeader
        icon={Activity}
        iconClass="text-emerald-600"
        title="Foundation Bridge Ops"
        hint="Bridge health · approval rules · sync"
        testId="toggle-foundation-bridge-ops"
        actions={<HealthChip data={health} />}
      />

      {true && (
        <div className="px-5 py-4 space-y-5" data-testid="foundation-bridge-ops-body">
          {!isAdmin && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm text-slate-500">
              Admin role required.
            </div>
          )}

          {isAdmin && (
            <>
              {/* Health detail */}
              <CollapsibleSection
                id="bridge-health" testId="bridge-health-section"
                icon={ShieldCheck} iconClass="text-emerald-600" title="Bridge connection"
                actions={<HealthChip data={health} />}
              >
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs space-y-1">
                  <div><strong>Status:</strong> <HealthChip data={health} /></div>
                  {health?.body && (
                    <div className="text-slate-500 font-mono">
                      {JSON.stringify(health.body, null, 0).slice(0, 240)}
                    </div>
                  )}
                  {health?.error && (
                    <div className="text-rose-600 font-mono">{String(health.error).slice(0, 240)}</div>
                  )}
                </div>
                <button
                  onClick={loadHealth}
                  className="mt-2 text-[11px] text-blue-600 hover:underline inline-flex items-center gap-1"
                  data-testid="btn-bridge-test-now"
                >
                  <RefreshCw className="w-3 h-3" /> Test now
                </button>
              </CollapsibleSection>

              {/* iter214 — Bridge Health widget: heartbeat + per-doc-type
                  INSERT lag + stale-queue warnings. Surfaces the #273
                  failure mode (stalled bridge / backed-up queue) before
                  the queue silently piles up. */}
              <CollapsibleSection
                id="bridge-telemetry" testId="bridge-telemetry-section"
                icon={Activity} iconClass="text-emerald-600" title="Bridge health"
                actions={
                  <button
                    onClick={loadTelemetry}
                    className="text-[11px] text-blue-600 hover:underline inline-flex items-center gap-1"
                    data-testid="btn-bridge-telemetry-refresh"
                  >
                    <RefreshCw className="w-3 h-3" /> Refresh
                  </button>
                }
              >

                {telemetry === null ? (
                  <div className="text-xs text-slate-400" data-testid="bridge-telemetry-loading">Loading…</div>
                ) : (
                  <>
                    {/* Heartbeat row */}
                    <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs"
                         data-testid="bridge-heartbeat-row">
                      <span
                        className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${
                          telemetry.overall === "ok" ? "bg-emerald-500"
                          : telemetry.overall === "warning" ? "bg-amber-500"
                          : "bg-rose-500"
                        }`}
                        data-testid={`bridge-heartbeat-dot-${telemetry.overall}`}
                      />
                      <span className="text-slate-700">
                        <strong>Last contract fetch:</strong>{" "}
                        {fmtAgo(telemetry.heartbeat?.last_contract_fetch_at)}
                        {telemetry.heartbeat?.contract_fetch_count > 0 &&
                          ` · ${telemetry.heartbeat.contract_fetch_count} fetch${telemetry.heartbeat.contract_fetch_count === 1 ? "" : "es"}`}
                      </span>
                    </div>

                    {/* Drift warning banner */}
                    {telemetry.heartbeat?.drift_warning && (
                      <div className="mt-2 flex items-start gap-2 bg-rose-50 border border-rose-200 text-rose-800 rounded-lg px-3 py-2 text-xs"
                           data-testid="bridge-drift-warning">
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        <span>
                          {telemetry.heartbeat?.last_contract_fetch_at
                            ? `The bridge hasn't fetched the contract in over ${telemetry.heartbeat.threshold_hours} h — it may be stalled or misconfigured.`
                            : "The bridge has never fetched the contract endpoint — the on-prem service may not be running the updated poller yet."}
                        </span>
                      </div>
                    )}

                    {/* Per-doc-type lag table */}
                    <div className="mt-2 overflow-hidden rounded-lg border border-slate-200">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50">
                          <tr className="text-left text-slate-500">
                            <th className="px-3 py-2 font-semibold">Doc Type</th>
                            <th className="px-3 py-2 font-semibold text-right">Queued</th>
                            <th className="px-3 py-2 font-semibold text-right">Failed</th>
                            <th className="px-3 py-2 font-semibold text-right">Last INSERT lag</th>
                            <th className="px-3 py-2 font-semibold">Last Sent</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {(telemetry.doc_types || []).map(d => (
                            <tr key={d.doc_type} data-testid={`telemetry-row-${d.doc_type}`}>
                              <td className="px-3 py-2">
                                <span className="text-slate-700">{DOC_TYPE_LABELS[d.doc_type] || d.doc_type}</span>
                                {d.parked && (
                                  <span className="ml-1.5 inline-flex items-center gap-1 text-[10px] font-medium bg-slate-100 text-slate-600 border border-slate-300 px-1.5 py-0.5 rounded-full"
                                        title={d.parked_note}
                                        data-testid={`telemetry-parked-${d.doc_type}`}>
                                    Parked
                                  </span>
                                )}
                                {d.queue_stale && (
                                  <span className="ml-1.5 inline-flex items-center gap-1 text-[10px] font-medium bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded-full"
                                        title={`Oldest queued row has waited ${d.oldest_pending_age_hours} h — the bridge may not be polling.`}
                                        data-testid={`telemetry-stale-${d.doc_type}`}>
                                    <AlertTriangle className="w-2.5 h-2.5" /> stale
                                  </span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums">{d.pending_bridge}</td>
                              <td className={`px-3 py-2 text-right tabular-nums ${d.failed > 0 ? "text-rose-600 font-semibold" : "text-slate-400"}`}>
                                {d.failed}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums">{fmtLag(d.last_insert_lag_seconds)}</td>
                              <td className="px-3 py-2 text-slate-500">{fmtAgo(d.last_sent_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </CollapsibleSection>

              {/* iter218 — On-demand Foundation schema check. The
                  DB-side companion to the heartbeat drift-guard: probes
                  the live SQL for every target table + pushed column so
                  admins catch renamed columns / dropped tables before a
                  push silently fails. */}
              <CollapsibleSection
                id="bridge-schema-check" testId="bridge-schema-check-section"
                icon={Database} iconClass="text-indigo-600" title="Schema check"
                extra={<>
                    {schemaCheck && schemaCheck.overall === "ok" && (
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full"
                            data-testid="schema-check-badge-ok">
                        <CheckCircle2 className="w-3 h-3" /> OK
                      </span>
                    )}
                    {schemaCheck && (schemaCheck.overall === "drift" || schemaCheck.overall === "error") && (
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded-full"
                            data-testid="schema-check-badge-drift">
                        <AlertTriangle className="w-3 h-3" /> {schemaCheck.overall === "drift" ? "Drift" : "Error"}
                      </span>
                    )}
                </>}
                actions={
                  <button
                    onClick={runSchemaCheck}
                    disabled={schemaChecking}
                    className="text-[11px] text-indigo-600 hover:underline inline-flex items-center gap-1 disabled:opacity-50"
                    data-testid="btn-schema-check"
                  >
                    <RefreshCw className={`w-3 h-3 ${schemaChecking ? "animate-spin" : ""}`} />
                    {schemaChecking ? "Checking…" : "Run check"}
                  </button>
                }
              >

                <p className="text-xs text-slate-500 mb-2">
                  Probes the live Foundation SQL (<code>WHERE 1=0</code>) to confirm every target table and pushed column exists — catches schema drift before a push fails.
                </p>

                {schemaCheck === null ? (
                  <div className="text-xs text-slate-400" data-testid="schema-check-idle">
                    Not run yet — click <span className="font-medium">Run check</span>.
                  </div>
                ) : schemaCheck.overall === "unconfigured" ? (
                  <div className="text-xs text-rose-600" data-testid="schema-check-unconfigured">
                    {schemaCheck.error || "Foundation bridge not configured."}
                  </div>
                ) : (
                  <div className="space-y-1.5" data-testid="schema-check-results">
                    {(schemaCheck.tables || []).map(t => (
                      <div key={t.doc_type}
                           className={`flex items-start gap-2 rounded-md border px-3 py-2 text-xs ${
                             t.status === "ok" ? "bg-emerald-50 border-emerald-200"
                             : t.status === "skipped" ? "bg-slate-50 border-slate-200"
                             : "bg-rose-50 border-rose-200"
                           }`}
                           data-testid={`schema-row-${t.doc_type}`}>
                        {t.status === "ok" ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
                        ) : t.status === "skipped" ? (
                          <Database className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
                        ) : (
                          <AlertTriangle className="w-3.5 h-3.5 text-rose-600 mt-0.5 shrink-0" />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="text-slate-700">
                            <span className="font-medium">{t.label || DOC_TYPE_LABELS[t.doc_type] || t.doc_type}</span>
                            {" → "}
                            <code className="text-slate-600">{t.table}</code>
                            {t.status === "ok" && t.doc_type !== "jobs_sp" && <span className="text-emerald-700"> · {t.expected_columns.length} columns verified</span>}
                            {t.parked && <span className="ml-1.5 text-[10px] font-medium bg-slate-100 text-slate-600 border border-slate-300 px-1.5 py-0.5 rounded-full">Parked</span>}
                          </div>
                          {t.checks && (
                            <div className="text-slate-600 mt-0.5" data-testid="schema-row-jobs-sp-checks">
                              {t.checks.parameters} params listed
                              {t.checks.undefaulted_extra_params > 0 && ` (${t.checks.undefaulted_extra_params} outside Phase 1 report no default — advisory)`}
                              {" · DB "}<code>{t.checks.database || "?"}</code>{t.checks.database_matches ? " ✓" : " ✗"}
                              {" · login "}<code>{t.checks.login || "?"}</code>
                              {" · EXECUTE "}{t.checks.can_execute ? "✓" : "✗"}
                            </div>
                          )}
                          {t.status === "blocked" && (t.blockers || []).map((b, i) => (
                            <div key={i} className="text-rose-700">{b}</div>
                          ))}
                          {t.status === "table_missing" && (
                            <div className="text-rose-700">Table not found in Foundation.</div>
                          )}
                          {t.status === "columns_missing" && (
                            <div className="text-rose-700">Missing columns: {t.missing_columns.join(", ")}</div>
                          )}
                          {t.status === "error" && (
                            <div className="text-rose-700 truncate">{t.error || "Probe error"}</div>
                          )}
                          {t.status === "skipped" && (
                            <div className="text-slate-500">{t.note || "Skipped"}</div>
                          )}
                        </div>
                      </div>
                    ))}
                    {schemaCheck.generated_at && (
                      <div className="text-[10px] text-slate-400 pt-0.5" data-testid="schema-check-ts">
                        Checked {fmtAgo(schemaCheck.generated_at)}
                      </div>
                    )}
                  </div>
                )}
              </CollapsibleSection>


              {/* iter231 — Write-permission audit: HAS_PERMS_BY_NAME matrix
                  for every push target table. Shows exact GRANT SQL for the
                  DBA when INSERT is denied. */}
              <CollapsibleSection
                id="bridge-write-perm" testId="bridge-write-perm-section"
                icon={ShieldCheck} iconClass="text-amber-600" title="Write permissions"
                extra={<>
                    {writePerms && !writePerms.error && writePerms.ok && (
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full"
                            data-testid="write-perm-badge-ok">
                        <CheckCircle2 className="w-3 h-3" /> OK
                      </span>
                    )}
                    {writePerms && !writePerms.error && !writePerms.ok && (
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full"
                            data-testid="write-perm-badge-missing">
                        <AlertTriangle className="w-3 h-3" /> Grants missing
                      </span>
                    )}
                </>}
                actions={
                  <button
                    onClick={runWritePermCheck}
                    disabled={writePermChecking}
                    className="text-[11px] text-amber-600 hover:underline inline-flex items-center gap-1 disabled:opacity-50"
                    data-testid="btn-write-perm-check"
                  >
                    <RefreshCw className={`w-3 h-3 ${writePermChecking ? "animate-spin" : ""}`} />
                    {writePermChecking ? "Checking…" : "Check now"}
                  </button>
                }
              >

                <p className="text-xs text-slate-500 mb-2">
                  Asks SQL Server (<code>HAS_PERMS_BY_NAME</code>) whether the bridge login can INSERT into every push target — catches missing GRANTs before a push fails.
                </p>

                {writePerms === null ? (
                  <div className="text-xs text-slate-400" data-testid="write-perm-idle">
                    Not run yet — click <span className="font-medium">Check now</span>.
                  </div>
                ) : writePerms.error ? (
                  <div className="text-xs text-rose-600" data-testid="write-perm-error">
                    {String(writePerms.error).slice(0, 300)}
                  </div>
                ) : (
                  <div className="space-y-1.5" data-testid="write-perm-results">
                    <div className="text-[11px] text-slate-500" data-testid="write-perm-context">
                      Login <code className="text-slate-700">{writePerms.login}</code>
                      {" · DB "}<code className="text-slate-700">{writePerms.database}</code>
                      {" · Server "}<code className="text-slate-700">{writePerms.server}</code>
                      {writePerms.roles && (
                        <> {" · datareader "}{writePerms.roles.db_datareader ? "✓" : "✗"}
                        {" · datawriter "}{writePerms.roles.db_datawriter ? "✓" : "✗"}</>
                      )}
                    </div>
                    {(writePerms.tables || []).map(t => (
                      <div key={t.table}
                           className={`rounded-md border px-3 py-2 text-xs ${
                             t.insert ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"
                           }`}
                           data-testid={`write-perm-row-${t.table}`}>
                        <div className="flex items-center gap-2 text-slate-700">
                          {t.insert
                            ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                            : <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0" />}
                          <code className="font-medium">{t.table}</code>
                          <span className="text-slate-500">({(t.doc_types || []).join(", ")})</span>
                          <span className="ml-auto font-mono text-[10px] text-slate-600">
                            SEL {t.select ? "✓" : "✗"} · INS {t.insert ? "✓" : "✗"} · UPD {t.update ? "✓" : "✗"} · DEL {t.delete ? "✓" : "✗"}
                          </span>
                        </div>
                        {!t.insert && t.grant_sql && (
                          <div className="mt-1 text-amber-800">
                            DBA fix: <code className="bg-white/70 px-1 py-0.5 rounded">{t.grant_sql}</code>
                          </div>
                        )}
                      </div>
                    ))}
                    {writePerms.generated_at && (
                      <div className="text-[10px] text-slate-400 pt-0.5" data-testid="write-perm-ts">
                        Checked {fmtAgo(writePerms.generated_at)}
                      </div>
                    )}
                  </div>
                )}
              </CollapsibleSection>

              {/* Foundation job creation Phase 1 — sp_insert_update_jobs
                  admin test screen + SP bridge log. */}
              <FoundationJobSpPanel />

              {/* Approval rules */}
              <CollapsibleSection
                id="bridge-approval" testId="bridge-approval-section"
                icon={ShieldCheck} iconClass="text-amber-600"
                title="Approval requirements per doc type"
              >
                <p className="text-xs text-slate-500 mb-2">
                  When ON, pushes from that doc type land in the Approval Queue and need a manager click before flowing to the bridge.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {approvalSettings === null ? (
                    <div className="col-span-2 text-xs text-slate-400">Loading…</div>
                  ) : Object.entries(DOC_TYPE_LABELS).filter(([dt]) => dt !== "jobs_sp").map(([dt, label]) => (
                    <label key={dt}
                           className="flex items-center justify-between gap-2 bg-slate-50 border border-slate-200 rounded-md px-3 py-2 cursor-pointer hover:bg-slate-100">
                      <span className="text-sm text-slate-700">{label}</span>
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-amber-600"
                        checked={!!approvalSettings[dt]}
                        onChange={e => handleToggleApproval(dt, e.target.checked)}
                        data-testid={`approval-toggle-${dt}`}
                      />
                    </label>
                  ))}
                </div>
              </CollapsibleSection>

              {/* Project type → Foundation job type mapping */}
              <CollapsibleSection
                id="bridge-project-type-map" testId="bridge-project-type-map-section"
                icon={Database} iconClass="text-indigo-600"
                title="Project type → Foundation job type"
              >
                <p className="text-xs text-slate-500 mb-2">
                  Values sent as <code>project_type</code> on the work order push. Replace the readable defaults with the real Foundation job-type codes when known.
                </p>
                {projectTypeMap === null ? (
                  <div className="text-xs text-slate-400">Loading…</div>
                ) : (
                  <div className="space-y-2">
                    {[["service", "Service"], ["service_contract", "Service Contract"], ["tm", "T&M"]].map(([key, label]) => (
                      <div key={key} className="flex items-center gap-3">
                        <span className="text-sm text-slate-700 w-36 shrink-0">{label}</span>
                        <input
                          value={projectTypeMap[key] || ""}
                          onChange={e => setProjectTypeMap(m => ({ ...m, [key]: e.target.value }))}
                          className="flex-1 border border-slate-200 rounded-md px-2 py-1.5 text-sm bg-slate-50 focus:bg-white focus:outline-none focus:ring-1 focus:ring-indigo-400"
                          data-testid={`project-type-map-input-${key}`}
                        />
                      </div>
                    ))}
                    <button
                      onClick={handleSaveProjectTypeMap}
                      disabled={savingPtMap}
                      className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white text-xs rounded-md px-3 py-1.5"
                      data-testid="btn-save-project-type-map"
                    >
                      {savingPtMap ? "Saving…" : "Save mapping"}
                    </button>
                  </div>
                )}
              </CollapsibleSection>

              {/* Sync + dispatch actions */}
              <CollapsibleSection
                id="bridge-actions" testId="bridge-actions-section"
                icon={Send} iconClass="text-emerald-600" title="Dispatch & pull"
              >
                <div className="flex gap-2">
                <button
                  onClick={handleDispatch}
                  disabled={busy || !health?.ok}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white text-sm rounded-md px-3 py-2 inline-flex items-center justify-center gap-2"
                  data-testid="btn-bridge-dispatch"
                >
                  <Send className="w-4 h-4" />
                  Dispatch approved batch
                </button>
                <button
                  onClick={handleSync}
                  disabled={busy || !health?.ok}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white text-sm rounded-md px-3 py-2 inline-flex items-center justify-center gap-2"
                  data-testid="btn-foundation-sync"
                >
                  <Database className="w-4 h-4" />
                  Pull latest from Foundation
                </button>
                </div>
              </CollapsibleSection>

              {/* iter255 — Backfill orphan Won proposals into Work Orders.
                  Historically Won proposals were marked without a WO — this
                  one-click converts them all and stamps foundation_sync_mode
                  so they either queue for Foundation or stay in-app only. */}
              <CollapsibleSection
                id="bridge-backfill" testId="bridge-backfill-section"
                icon={AlertTriangle} iconClass="text-amber-600" title="Backfill Won → Work Orders"
                defaultOpen={false}
              >
                <div className="text-[11px] text-amber-800 mb-2">
                  Convert every WON proposal that never got a Work Order. Existing WOs are untouched.
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={async () => {
                      setBusy(true);
                      try {
                        const { data } = await backfillWonOrphans({ dry_run: true });
                        toast.info(`${data.orphan_count} orphan Won proposals found${data.sample?.length ? " — sample: " + data.sample.slice(0,3).map(s => s.name).join(", ") : ""}`);
                      } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
                      finally { setBusy(false); }
                    }}
                    disabled={busy}
                    className="text-xs bg-white border border-amber-300 rounded-md px-2 py-1 hover:bg-amber-100"
                    data-testid="btn-backfill-preview"
                  >Preview Count</button>
                  <button
                    onClick={async () => {
                      if (!window.confirm("Convert ALL orphan Won proposals to Work Orders and mark them In-app only?\n\nThis is safe — existing WOs are untouched. Use Foundation Bridge Ops to re-queue individual WOs later if needed.")) return;
                      setBusy(true);
                      try {
                        const { data } = await backfillWonOrphans({ foundation_sync_mode: "skipped" });
                        toast.success(`Converted ${data.converted_count} · Failed ${data.failed_count}`);
                      } catch (e) { toast.error(e?.response?.data?.detail || "Backfill failed"); }
                      finally { setBusy(false); }
                    }}
                    disabled={busy}
                    className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded-md px-2 py-1"
                    data-testid="btn-backfill-inapp"
                  >Convert all → In-app only</button>
                  <button
                    onClick={async () => {
                      if (!window.confirm("Convert ALL orphan Won proposals and queue them for Foundation? Only do this once the Foundation bridge is healthy.")) return;
                      setBusy(true);
                      try {
                        const { data } = await backfillWonOrphans({ foundation_sync_mode: "queued" });
                        toast.success(`Converted ${data.converted_count} · Failed ${data.failed_count}`);
                      } catch (e) { toast.error(e?.response?.data?.detail || "Backfill failed"); }
                      finally { setBusy(false); }
                    }}
                    disabled={busy}
                    className="text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-md px-2 py-1"
                    data-testid="btn-backfill-queued"
                  >Convert all → Queue for Foundation</button>
                </div>
              </CollapsibleSection>

              {/* Sync status table */}
              <CollapsibleSection
                id="bridge-sync-status" testId="bridge-sync-status-section"
                icon={Database} iconClass="text-slate-500" title="Foundation cache"
                extra={<>
                  {pkIntegrityOverall === "warning" && (
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 rounded-full"
                          title="One or more tables show <95% retention. Inspect the PK column below."
                          data-testid="pk-integrity-pill-warning">
                      <AlertTriangle className="w-3 h-3" /> PK integrity: warning
                    </span>
                  )}
                  {pkIntegrityOverall === "critical" && (
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium bg-rose-50 text-rose-800 border border-rose-200 px-2 py-0.5 rounded-full"
                          title="One or more tables show <80% retention. The natural-PK rule is too narrow — silent row drop. Inspect the PK column below."
                          data-testid="pk-integrity-pill-critical">
                      <AlertTriangle className="w-3 h-3" /> PK integrity: CRITICAL
                    </span>
                  )}
                  {pkIntegrityOverall === "ok" && (
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-0.5 rounded-full"
                          title="All tables retain ≥95% of source rows after PK collapse."
                          data-testid="pk-integrity-pill-ok">
                      <CheckCircle2 className="w-3 h-3" /> PK integrity: ok
                    </span>
                  )}
                </>}
              >
                <div className="overflow-hidden rounded-lg border border-slate-200">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50">
                      <tr className="text-left text-slate-500">
                        <th className="px-3 py-2 font-semibold">Table</th>
                        <th className="px-3 py-2 font-semibold">Last Sync</th>
                        <th className="px-3 py-2 font-semibold text-right">Rows</th>
                        <th className="px-3 py-2 font-semibold">PK</th>
                        <th className="px-3 py-2 font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {(syncStatus || []).map(t => (
                        <tr key={t.table} data-testid={`sync-row-${t.table}`}>
                          <td className="px-3 py-2 font-mono">{t.table}</td>
                          <td className="px-3 py-2 text-slate-500">{fmtAgo(t.last_sync_at)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{t.live_count ?? "—"}</td>
                          {/* iter197 — PK integrity badge per row.
                              ok → tiny green check; warning/critical
                              show retention% + collapsed-row count so
                              the operator can immediately see HOW
                              MUCH data is being silently dropped. */}
                          <td className="px-3 py-2">
                            {(() => {
                              const pk = t.pk_integrity || {};
                              if (pk.status === "ok") {
                                return (
                                  <span className="inline-flex items-center gap-1 text-emerald-700"
                                        title={`${pk.retention_pct}% retention`}
                                        data-testid={`pk-cell-${t.table}-ok`}>
                                    <CheckCircle2 className="w-3 h-3" />
                                  </span>
                                );
                              }
                              if (pk.status === "warning") {
                                return (
                                  <span className="inline-flex items-center gap-1 text-amber-700 tabular-nums text-[11px]"
                                        title={`${pk.collapsed_rows} of ${(pk.collapsed_rows || 0) + (t.live_count || 0)} source rows collapsed by the natural-PK formula. Investigate _natural_pk() for this table.`}
                                        data-testid={`pk-cell-${t.table}-warning`}>
                                    🚩 {pk.retention_pct}% (-{pk.collapsed_rows})
                                  </span>
                                );
                              }
                              if (pk.status === "critical") {
                                return (
                                  <span className="inline-flex items-center gap-1 text-rose-700 font-semibold tabular-nums text-[11px]"
                                        title={`${pk.collapsed_rows} rows dropped. _natural_pk() is too narrow — widen the composite or add a line-id column.`}
                                        data-testid={`pk-cell-${t.table}-critical`}>
                                    🚨 {pk.retention_pct}% (-{pk.collapsed_rows})
                                  </span>
                                );
                              }
                              return <span className="text-slate-300" data-testid={`pk-cell-${t.table}-unknown`}>—</span>;
                            })()}
                          </td>
                          <td className="px-3 py-2">
                            {t.last_sync_status === "ok" ? (
                              <span className="text-emerald-600">ok</span>
                            ) : t.last_sync_status === "error" ? (
                              <span className="text-rose-600" title={t.error}>error</span>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                      {syncStatus && syncStatus.length === 0 && (
                        <tr>
                          <td colSpan={5} className="px-3 py-6 text-center text-slate-400">
                            No sync history yet — click &quot;Pull latest from Foundation&quot;.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CollapsibleSection>
            </>
          )}
        </div>
      )}
    </div>
  );
}
